import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { shareReplay, catchError } from 'rxjs/operators';
import { Utils } from '../utils/utils';

export interface MersyPersonae {
    persona: string;
    id: string;
}

export interface MersyRolesData {
    personae: MersyPersonae[];
}

export interface FilteredRoles {
    mersyRoles: MersyPersonae[];
    marketQueryRoles: MersyPersonae[];
    globalBranchRoles: MersyPersonae[];
    niOpsRoles: MersyPersonae[];
    taxRoles: MersyPersonae[];
    safetySystemExcellenceRoles: MersyPersonae[];
    incoRoles: MersyPersonae[];
}

@Injectable({
    providedIn: 'root'
})
export class SharedMersyRolesService {
    private dataCache$: Observable<MersyRolesData> | null = null;
    private filteredRolesSubject = new BehaviorSubject<FilteredRoles | null>(null);
    private errorSubject = new BehaviorSubject<string | null>(null);
    private loadingSubject = new BehaviorSubject<boolean>(false);

    public filteredRoles$ = this.filteredRolesSubject.asObservable();
    public error$ = this.errorSubject.asObservable();
    public loading$ = this.loadingSubject.asObservable();

    constructor(private http: HttpClient) {}

    public loadMersyRoles(baseUrl: string, userId: string = ''): void {
        if (this.dataCache$) {
            return;
        }

        this.loadingSubject.next(true);
        this.errorSubject.next(null);

        const url = baseUrl + Utils.getUMPUser() + "/mersypersonae";

        this.dataCache$ = this.http.get<MersyRolesData>(url).pipe(
            shareReplay(1),
            catchError(error => {
                Utils.writeLog(this.http, baseUrl, error);
                this.errorSubject.next(error.message || 'An error occurred while loading Mersy roles');
                this.loadingSubject.next(false);
                return of({ personae: [] });
            })
        );

        this.dataCache$.subscribe(
            (data: MersyRolesData) => {
                this.processAndFilterData(data);
                this.loadingSubject.next(false);
            },
            (error) => {
                this.errorSubject.next(error.message || 'An error occurred while processing Mersy roles');
                this.loadingSubject.next(false);
            }
        );
    }

    private processAndFilterData(data: MersyRolesData): void {
        const allRoles = data.personae || [];

        const filteredRoles: FilteredRoles = {
            mersyRoles: allRoles.filter(obj =>
                !obj.persona.toLowerCase().includes("marketquery")
                && !obj.persona.toLowerCase().includes("branchexcellence")
                && !obj.persona.toLowerCase().includes("niops")
                && !obj.persona.toLowerCase().includes("tax")
                && !obj.persona.toLowerCase().includes("safetysystemexcellence")
                && !obj.persona.toLowerCase().includes("inco")
            ),
            marketQueryRoles: allRoles.filter(obj =>
                obj.persona.toLowerCase().includes("marketquery")
            ),
            globalBranchRoles: allRoles.filter(obj =>
                obj.persona.toLowerCase().includes("branchexcellence")
            ),
            niOpsRoles: allRoles.filter(obj =>
                obj.persona.toLowerCase().includes("niops")
            ),
            taxRoles: allRoles.filter(obj =>
                obj.persona.toLowerCase().includes("tax")
            ),
            safetySystemExcellenceRoles: allRoles.filter(obj =>
                obj.persona.toLowerCase().includes("safetysystemexcellence")
            ),
            incoRoles: allRoles.filter(obj =>
                obj.persona.toLowerCase().includes("inco")
            )
        };

        this.filteredRolesSubject.next(filteredRoles);
    }

    public refreshData(baseUrl: string, userId: string = ''): void {
        this.dataCache$ = null;
        this.filteredRolesSubject.next(null);
        this.loadMersyRoles(baseUrl, userId);
    }

    public getCurrentFilteredRoles(): FilteredRoles | null {
        return this.filteredRolesSubject.value;
    }

    public isLoading(): boolean {
        return this.loadingSubject.value;
    }

    public getCurrentError(): string | null {
        return this.errorSubject.value;
    }

    public clearError(): void {
        this.errorSubject.next(null);
    }
}
